import {
  normalizeSnapshot,
  type PostSnapshot
} from '@shared/all/helpers/postSnapshot';
import {
  and,
  asc,
  between,
  count,
  desc,
  eq,
  inArray,
  isNull,
  or,
  sql
} from 'drizzle-orm';

import { SYNC_TABLES } from '../helpers/syncTypes';
import { computeHash } from './computeHash';
import type { SourceType } from './schema';
import * as schema from './schema';
import {
  clearTombstones,
  deleteEachWithTombstones,
  deleteWithTombstones,
  isSyncTableName
} from './tombstones';
import type { DrizzleDB, MoodType, TimeOfDayType, UploadType } from './types';

function getTimeOfDay(timestamp: number): TimeOfDayType {
  const hours = new Date(timestamp).getHours();
  if (hours >= 6 && hours < 12) return 'morning';
  if (hours >= 12 && hours < 18) return 'afternoon';
  if (hours >= 18 && hours < 24) return 'evening';
  return 'night';
}

export class DatabaseRepository {
  private db: DrizzleDB;
  private source: SourceType;

  constructor(db: DrizzleDB, source: SourceType) {
    this.db = db;
    this.source = source;
  }

  // ============================================
  // Post Engagements
  // ============================================

  async addOrUpdatePostEngagement(
    postId: string,
    duration: number,
    viewedAtMs: number
  ): Promise<void> {
    const existing = await this.db
      .select()
      .from(schema.postEngagements)
      .where(eq(schema.postEngagements.postId, postId))
      .limit(1);

    if (existing.length > 0) {
      await this.db
        .update(schema.postEngagements)
        .set({
          viewedAt: viewedAtMs,
          duration: existing[0].duration + duration
        })
        .where(eq(schema.postEngagements.id, existing[0].id));
    } else {
      await this.db.insert(schema.postEngagements).values({
        postId,
        viewedAt: viewedAtMs,
        duration,
        source: this.source
      });
    }
  }

  async getPostEngagement(postId: string) {
    const rows = await this.db
      .select()
      .from(schema.postEngagements)
      .where(eq(schema.postEngagements.postId, postId))
      .limit(1);

    if (rows.length === 0) return null;
    const r = rows[0];
    return {
      id: r.id,
      postId: r.postId,
      viewedAt: r.viewedAt,
      duration: r.duration,
      source: r.source
    };
  }

  async getAllPostEngagements() {
    const rows = await this.db
      .select()
      .from(schema.postEngagements)
      .orderBy(desc(schema.postEngagements.createdAt))
      .limit(10000);

    return rows.map((r) => ({
      id: r.id,
      postId: r.postId,
      viewedAt: r.viewedAt,
      duration: r.duration,
      source: r.source
    }));
  }

  async getPaginatedPostEngagements(page: number, limit = 20) {
    const rows = await this.db
      .select()
      .from(schema.postEngagements)
      .orderBy(desc(schema.postEngagements.duration))
      .offset(page * limit)
      .limit(limit);

    return rows.map((r) => ({
      id: r.id,
      postId: r.postId,
      viewedAt: r.viewedAt,
      duration: r.duration,
      source: r.source
    }));
  }

  // ============================================
  // Factiii Engagements
  // ============================================

  async addOrUpdateFactiiiEngagement(
    factiiiId: number,
    name: string,
    duration: number,
    viewedAtMs: number
  ): Promise<void> {
    const viewedAtDate = new Date(viewedAtMs);

    // Calculate day boundaries for same-day deduplication
    const dayStart = new Date(viewedAtDate);
    dayStart.setHours(0, 0, 0, 0);
    const dayEnd = new Date(viewedAtDate);
    dayEnd.setHours(23, 59, 59, 999);

    const existing = await this.db
      .select()
      .from(schema.factiiiEngagements)
      .where(
        and(
          eq(schema.factiiiEngagements.factiiiId, factiiiId),
          between(
            schema.factiiiEngagements.viewedAt,
            dayStart.getTime(),
            dayEnd.getTime()
          )
        )
      )
      .limit(1);

    if (existing.length > 0) {
      const first = existing[0];
      // Skip if too recent (within 3 seconds)
      if (Date.now() - first.viewedAt < 3000) return;

      await this.db
        .update(schema.factiiiEngagements)
        .set({
          viewedAt: viewedAtMs,
          duration: first.duration + duration
        })
        .where(eq(schema.factiiiEngagements.id, first.id));
    } else {
      await this.db.insert(schema.factiiiEngagements).values({
        factiiiId,
        factiiiName: name,
        viewedAt: viewedAtMs,
        duration,
        source: this.source
      });
    }
  }

  async getAllFactiiiEngagements() {
    const rows = await this.db
      .select()
      .from(schema.factiiiEngagements)
      .orderBy(desc(schema.factiiiEngagements.createdAt))
      .limit(10000);

    return rows.map((r) => ({
      id: r.id,
      factiiiId: r.factiiiId,
      factiiiName: r.factiiiName,
      viewedAt: r.viewedAt,
      duration: r.duration,
      source: r.source
    }));
  }

  async getFactiiiEngagementsByDateRange(startDate: Date, endDate: Date) {
    const start = new Date(startDate);
    start.setHours(0, 0, 0, 0);
    const end = new Date(endDate);
    end.setHours(23, 59, 59, 999);

    const rows = await this.db
      .select()
      .from(schema.factiiiEngagements)
      .where(
        between(
          schema.factiiiEngagements.viewedAt,
          start.getTime(),
          end.getTime()
        )
      );

    return rows.map((r) => ({
      id: r.id,
      factiiiId: r.factiiiId,
      factiiiName: r.factiiiName,
      viewedAt: r.viewedAt,
      duration: r.duration,
      source: r.source
    }));
  }

  // ============================================
  // Mood Entries
  // ============================================

  async addMoodEntry(mood: MoodType, date?: Date): Promise<void> {
    const now = date || new Date();
    const ts = now.getTime();
    await this.db.insert(schema.moodEntries).values({
      mood,
      timestamp: ts,
      source: this.source
    });
  }

  async getMoodEntriesByDateRange(startDate: Date, endDate: Date) {
    const rows = await this.db
      .select()
      .from(schema.moodEntries)
      .where(
        between(
          schema.moodEntries.timestamp,
          startDate.getTime(),
          endDate.getTime()
        )
      );

    return rows.map((r) => ({
      id: r.id,
      mood: r.mood as MoodType,
      timestamp: r.timestamp,
      timeOfDay: getTimeOfDay(r.timestamp),
      source: r.source
    }));
  }

  async getLatestMoodEntry() {
    const rows = await this.db
      .select()
      .from(schema.moodEntries)
      .orderBy(desc(schema.moodEntries.timestamp))
      .limit(1);

    if (rows.length === 0) return null;
    const r = rows[0];
    return {
      id: r.id,
      mood: r.mood as MoodType,
      timestamp: r.timestamp,
      timeOfDay: getTimeOfDay(r.timestamp),
      source: r.source
    };
  }

  // ============================================
  // Private Posts — Read
  // ============================================

  /**
   * Build a full PrivatePost object from a raw post row,
   * using a single relational query to fetch all nested data.
   */
  async buildPrivatePost(
    postRow: typeof schema.privatePosts.$inferSelect,
    userId: number,
    includeReplies = false
  ): Promise<PrivatePost> {
    const [result, repliesCount] = await Promise.all([
      this.db.query.privatePosts.findFirst({
        where: eq(schema.privatePosts.id, postRow.id),
        with: {
          uploads: true,
          factiiiCounts: {
            with: {
              factiiis: {
                with: {
                  spaceTimes: true,
                  uploadJunctions: { with: { upload: true } },
                  postReferences: true,
                  factiiiReferences: {
                    with: {
                      targetFactiii: {
                        with: {
                          spaceTimes: true,
                          uploadJunctions: { with: { upload: true } }
                        }
                      }
                    }
                  }
                }
              }
            }
          },
          ...(includeReplies ? { replies: true } : {})
        }
      }),
      this.db
        .select({ count: count() })
        .from(schema.privatePosts)
        .where(eq(schema.privatePosts.parentPostId, postRow.id))
    ]);
    if (!result) {
      return {
        id: postRow.id,
        content: postRow.content,
        anonymous: postRow.anonymous,
        spaceId: postRow.spaceId,
        userId: postRow.userId,
        parentPostId: postRow.parentPostId,
        slug: postRow.slug,
        source: postRow.source,
        createdAt: new Date(postRow.createdAt),
        updatedAt: new Date(postRow.updatedAt),
        uploads: [],
        factiiisCount: [],
        replies_count: 0,
        replies: []
      };
    }

    const repliesBuilt =
      includeReplies && result.replies
        ? await Promise.all(
            result.replies.map((r) => this.buildPrivatePost(r, userId, false))
          )
        : [];

    return {
      id: result.id,
      content: result.content,
      anonymous: result.anonymous,
      spaceId: result.spaceId,
      userId: result.userId,
      parentPostId: result.parentPostId,
      slug: result.slug,
      source: result.source,
      createdAt: new Date(result.createdAt),
      updatedAt: new Date(result.updatedAt),
      uploads: (result.uploads || []).map((u) => ({
        id: u.id,
        postId: u.postId,
        key: u.key,
        type: u.type as UploadType,
        source: u.source,
        createdAt: new Date(u.createdAt)
      })),
      factiiisCount: (result.factiiiCounts || []).map((fc) => ({
        id: fc.factiiiId,
        postId: fc.postId,
        factiiiId: fc.factiiiId,
        count: fc.count,
        source: fc.source,
        createdAt: new Date(fc.createdAt),
        updatedAt: new Date(fc.updatedAt),
        postFactiiis: (fc.factiiis || []).map((pf, pfIdx) => ({
          id: pfIdx,
          postFactiiiCountId: fc.factiiiId,
          anonymous: pf.anonymous,
          data: pf.data,
          number: pf.number,
          spaceTimes: (pf.spaceTimes || []).map((st, stIdx) => ({
            id: stIdx,
            timestamp: st.timestamp ? new Date(st.timestamp) : null,
            latitude: st.latitude,
            longitude: st.longitude,
            display: st.display,
            altitude: st.altitude,
            createdAt: new Date(st.createdAt),
            updatedAt: new Date(st.updatedAt)
          })),
          source: pf.source,
          createdAt: new Date(pf.createdAt),
          updatedAt: new Date(pf.updatedAt),
          uploads: (pf.uploadJunctions || [])
            .filter((j) => j.upload)
            .map((j) => ({
              id: j.upload!.id,
              postId: j.upload!.postId,
              key: j.upload!.key,
              type: j.upload!.type as UploadType,
              source: j.upload!.source,
              createdAt: new Date(j.upload!.createdAt)
            })),
          references: (pf.postReferences || []).map((ref) => ({
            uuid: ref.postUuid
          })),
          factiiiReferences: (pf.factiiiReferences || []).map((ref) => ({
            factiiiId: ref.factiiiId,
            data: ref.targetFactiii?.data ?? null,
            spaceTimes: (ref.targetFactiii?.spaceTimes || []).map(
              (st, stIdx) => ({
                id: stIdx,
                timestamp: st.timestamp ? new Date(st.timestamp) : null,
                latitude: st.latitude,
                longitude: st.longitude,
                display: st.display,
                altitude: st.altitude,
                createdAt: new Date(st.createdAt),
                updatedAt: new Date(st.updatedAt)
              })
            ),
            uploads: (ref.targetFactiii?.uploadJunctions || [])
              .filter((j) => j.upload)
              .map((j) => ({
                id: j.upload!.id,
                key: j.upload!.key
              }))
          }))
        }))
      })),
      replies_count: repliesCount[0]?.count ?? 0,
      replies: repliesBuilt
    };
  }

  /**
   * Fetch paginated top-level private posts (no parent)
   */
  fetchPrivatePosts(page: number, limit = 10) {
    return this.db
      .select()
      .from(schema.privatePosts)
      .where(isNull(schema.privatePosts.parentPostId))
      .orderBy(desc(schema.privatePosts.createdAt))
      .offset(page * limit)
      .limit(limit);
  }

  /**
   * Fetch a single post by ID
   */
  async fetchPrivatePost(postId: string) {
    const rows = await this.db
      .select()
      .from(schema.privatePosts)
      .where(eq(schema.privatePosts.id, postId))
      .limit(1);

    return rows[0] ?? null;
  }

  /**
   * Fetch paginated replies for a post
   */
  fetchPrivatePostReplies(postId: string, page: number, limit = 10) {
    return this.db
      .select()
      .from(schema.privatePosts)
      .where(eq(schema.privatePosts.parentPostId, postId))
      .orderBy(asc(schema.privatePosts.createdAt))
      .offset(page * limit)
      .limit(limit);
  }

  /**
   * Check if a post's content was actually edited by comparing snapshot contents.
   * Returns true only if snapshots exist with different content values.
   */
  async wasContentEdited(postId: string): Promise<boolean> {
    const result = await this.db
      .select({
        distinctCount: sql<number>`count(distinct ${schema.privatePostSnapshots.content})`
      })
      .from(schema.privatePostSnapshots)
      .where(eq(schema.privatePostSnapshots.postId, postId));

    return (result[0]?.distinctCount ?? 0) > 1;
  }

  // ============================================
  // Private Posts — Write
  // ============================================

  /**
   * Create a private post with uploads and factiiis.
   * Returns the raw post row (caller handles blob storage and hashing).
   */
  async createPrivatePost(params: {
    content: string;
    anonymous: boolean;
    userId: number;
    spaceId?: number;
    parentPostId?: string;
    uploadRecords: Array<{ key: string; type: string }>;
    factiiis: Array<{
      factiiiId: number;
      postFactiiis: Array<{
        anonymous?: boolean;
        data?: string | null;
        number?: number | null;
        spaceTimes?: Array<{
          timestamp?: Date | null;
          latitude?: number | null;
          longitude?: number | null;
          display?: string | null;
          altitude?: number | null;
        }>;
        photo?: { uploadKey: string; uploadType: string } | null;
        photoUploadRecord?: { key: string; type: string } | null;
        references?: Array<{ uuid: string }>;
        factiiiReferences?: Array<{ factiiiId: number; data?: string | null }>;
      }>;
    }>;
  }): Promise<string> {
    // Create post
    const [{ id: postId }] = await this.db
      .insert(schema.privatePosts)
      .values({
        content: params.content,
        anonymous: params.anonymous,
        spaceId: params.spaceId ?? null,
        userId: params.userId,
        parentPostId: params.parentPostId ?? null,
        source: this.source
      })
      .returning({ id: schema.privatePosts.id });

    // Create upload records
    for (const upload of params.uploadRecords) {
      await this.db.insert(schema.privateUploads).values({
        postId,
        key: upload.key,
        type: upload.type,
        source: this.source
      });
    }

    // Create factiiis
    for (const factiii of params.factiiis) {
      const [{ id: fcId }] = await this.db
        .insert(schema.privatePostFactiiiCounts)
        .values({
          postId,
          factiiiId: factiii.factiiiId,
          count: 1,
          source: this.source
        })
        .returning({ id: schema.privatePostFactiiiCounts.id });

      for (const pf of factiii.postFactiiis) {
        const [{ id: pfId }] = await this.db
          .insert(schema.privatePostFactiiis)
          .values({
            postFactiiiCountId: fcId,
            anonymous: pf.anonymous || false,
            data: pf.data ?? null,
            number: pf.number ?? null,
            source: this.source
          })
          .returning({ id: schema.privatePostFactiiis.id });

        // Create factiii photo upload if present
        if (pf.photoUploadRecord) {
          const [{ id: uploadId }] = await this.db
            .insert(schema.privateUploads)
            .values({
              key: pf.photoUploadRecord.key,
              type: pf.photoUploadRecord.type,
              source: this.source
            })
            .returning({ id: schema.privateUploads.id });

          await this.db.insert(schema.privatePostFactiiiUploads).values({
            postFactiiiId: pfId,
            uploadId,
            source: this.source
          });
        }

        // Create space times
        if (pf.spaceTimes?.length) {
          for (const st of pf.spaceTimes) {
            await this.db.insert(schema.spaceTimes).values({
              postFactiiiId: pfId,
              timestamp: st.timestamp?.getTime() ?? null,
              latitude: st.latitude ?? null,
              longitude: st.longitude ?? null,
              display: st.display ?? null,
              altitude: st.altitude ?? null,
              source: this.source
            });
          }
        }

        // Create post references
        if (pf.references?.length) {
          for (const ref of pf.references) {
            await this.db
              .insert(schema.privatePostFactiiiPostReferences)
              .values({
                postFactiiiId: pfId,
                postUuid: ref.uuid,
                source: this.source
              });
          }
        }

        // Create factiii references
        if (pf.factiiiReferences?.length) {
          for (const ref of pf.factiiiReferences) {
            const [{ id: targetPfId }] = await this.db
              .insert(schema.privatePostFactiiis)
              .values({
                anonymous: false,
                data: ref.data ?? null,
                source: this.source
              })
              .returning({ id: schema.privatePostFactiiis.id });

            await this.db.insert(schema.privatePostFactiiiReferences).values({
              factiiiId: ref.factiiiId,
              sourcePostFactiiiId: pfId,
              targetPostFactiiiId: targetPfId,
              source: this.source
            });
          }
        }
      }
    }

    return postId;
  }

  /**
   * Update post content
   */
  async updatePostContent(postId: string, content: string): Promise<void> {
    await this.db
      .update(schema.privatePosts)
      .set({ content })
      .where(eq(schema.privatePosts.id, postId));
  }

  /**
   * Delete all uploads for a post (DB records only)
   * Returns the blob keys for blob storage cleanup
   */
  async deleteUploadsForPost(postId: string): Promise<string[]> {
    const uploads = await this.db
      .select({ id: schema.privateUploads.id, key: schema.privateUploads.key })
      .from(schema.privateUploads)
      .where(eq(schema.privateUploads.postId, postId));

    const keys = uploads.map((u) => u.key);

    if (keys.length > 0) {
      // Each upload is its own delete, with a tombstone so it does not come
      // back from the other device (tombstones.ts)
      await deleteEachWithTombstones(
        this.db,
        uploads.map((u) => ({ table: SYNC_TABLES.PRIVATE_UPLOADS, id: u.id }))
      );
    }

    return keys;
  }

  /**
   * Create new upload records for a post
   */
  async createUploadRecords(
    postId: string,
    records: Array<{ key: string; type: string }>
  ): Promise<Array<{ id: string; key: string; createdAt: number }>> {
    const now = Date.now();
    const results: Array<{ id: string; key: string; createdAt: number }> = [];

    for (const record of records) {
      const [{ id }] = await this.db
        .insert(schema.privateUploads)
        .values({
          postId,
          key: record.key,
          type: record.type,
          source: this.source
        })
        .returning({ id: schema.privateUploads.id });
      results.push({ id, key: record.key, createdAt: now });
    }

    return results;
  }

  /**
   * Create an upload record (standalone, for uploadFromBase64)
   */
  async createUploadRecord(params: {
    key: string;
    type: string;
    postId?: string;
  }): Promise<{ id: string; createdAt: number }> {
    const now = Date.now();
    const [{ id }] = await this.db
      .insert(schema.privateUploads)
      .values({
        postId: params.postId ?? null,
        key: params.key,
        type: params.type,
        source: this.source
      })
      .returning({ id: schema.privateUploads.id });
    return { id, createdAt: now };
  }

  /**
   * Delete a private post and all its related records.
   * FK CASCADE handles child table cleanup (factiiiCounts, factiiis, junctions,
   * spaceTimes, snapshots + snapshot children). Uploads with postId get SET NULL,
   * so we delete them explicitly.
   * Every synced row that goes (the post, its uploads, and the cascaded
   * factiiiCounts, factiiis, junctions, spaceTimes and replies) gets a
   * tombstone under the post, written before the rows are deleted
   * (tombstones.ts), so the delete reaches the other device as one delete.
   * Returns snapshot IDs + blob keys for hash/blob cleanup by caller.
   */
  async deletePrivatePost(postId: string): Promise<{
    blobKeysToDelete: string[];
    hashIdsToDelete: string[];
  }> {
    // Collect the post's uploads before delete (uploads get SET NULL, not cascade deleted)
    const uploads = await this.db
      .select({ id: schema.privateUploads.id })
      .from(schema.privateUploads)
      .where(eq(schema.privateUploads.postId, postId));

    // Deletes the uploads explicitly (FK is SET NULL, not CASCADE) and the
    // synced child records children-first, then the post. FK CASCADE on the
    // post still handles the tables that do not sync:
    // snapshots -> snapshotUploads, snapshotFactiiiCounts -> snapshotFactiiis -> snapshotSpaceTimes, snapshotFactiiiUploads
    // Blob keys and snapshot IDs (for hash deletion) are collected before the
    // rows go.
    const { blobKeys, hashIds } = await deleteWithTombstones(
      this.db,
      { table: SYNC_TABLES.PRIVATE_POSTS, id: postId },
      uploads.map((u) => ({ table: SYNC_TABLES.PRIVATE_UPLOADS, id: u.id }))
    );

    return { blobKeysToDelete: blobKeys, hashIdsToDelete: hashIds };
  }

  // ============================================
  // Factiii Processing
  // ============================================

  async addFactiiis(
    postId: string,
    factiiis: Array<{
      factiiiId: number;
      anonymous?: boolean;
      data?: string | null;
      number?: number | null;
      spaceTimes?: Array<{
        timestamp?: Date | null;
        latitude?: number | null;
        longitude?: number | null;
        display?: string | null;
        altitude?: number | null;
      }>;
      uploads?: Array<{ id: string }>;
      references?: Array<{ uuid: string }>;
      factiiiReferences?: Array<{
        factiiiId: number;
        data?: string | null;
      }>;
    }>
  ): Promise<void> {
    for (const factiii of factiiis) {
      const [{ id: fcId }] = await this.db
        .insert(schema.privatePostFactiiiCounts)
        .values({
          postId,
          factiiiId: factiii.factiiiId,
          count: 1,
          source: this.source
        })
        .returning({ id: schema.privatePostFactiiiCounts.id });

      const [{ id: pfId }] = await this.db
        .insert(schema.privatePostFactiiis)
        .values({
          postFactiiiCountId: fcId,
          anonymous: factiii.anonymous || false,
          data: factiii.data ?? null,
          number: factiii.number ?? null,
          source: this.source
        })
        .returning({ id: schema.privatePostFactiiis.id });

      // Space times
      if (factiii.spaceTimes?.length) {
        for (const st of factiii.spaceTimes) {
          const tsValue = st.timestamp
            ? st.timestamp instanceof Date
              ? st.timestamp.getTime()
              : new Date(st.timestamp as unknown as string).getTime()
            : null;
          await this.db.insert(schema.spaceTimes).values({
            postFactiiiId: pfId,
            timestamp: tsValue,
            latitude: st.latitude ?? null,
            longitude: st.longitude ?? null,
            display: st.display ?? null,
            altitude: st.altitude ?? null,
            source: this.source
          });
        }
      }

      // Upload junctions
      if (factiii.uploads?.length) {
        for (const upload of factiii.uploads) {
          await this.db.insert(schema.privatePostFactiiiUploads).values({
            postFactiiiId: pfId,
            uploadId: upload.id,
            source: this.source
          });
        }
      }

      // Post references (for POST_REFERENCE_REQUIRED factiiis)
      if (factiii.references?.length) {
        for (const ref of factiii.references) {
          await this.db.insert(schema.privatePostFactiiiPostReferences).values({
            postFactiiiId: pfId,
            postUuid: ref.uuid,
            source: this.source
          });
        }
      }

      // Factiii references (for SOURCE_REQUIRED factiiis)
      if (factiii.factiiiReferences?.length) {
        for (const ref of factiii.factiiiReferences) {
          // Create a target postFactiii for each reference (no postFactiiiCountId since it's a reference)
          const [{ id: targetPfId }] = await this.db
            .insert(schema.privatePostFactiiis)
            .values({
              anonymous: false,
              data: ref.data ?? null,
              source: this.source
            })
            .returning({ id: schema.privatePostFactiiis.id });

          // Link source to target via the reference table
          await this.db.insert(schema.privatePostFactiiiReferences).values({
            factiiiId: ref.factiiiId,
            sourcePostFactiiiId: pfId,
            targetPostFactiiiId: targetPfId,
            source: this.source
          });
        }
      }
    }
  }

  async updateFactiiis(
    postId: string,
    factiiis: Array<{
      factiiiId: number;
      anonymous?: boolean;
      data?: string | null;
      number?: number | null;
      spaceTimes?: Array<{
        timestamp?: Date | null;
        latitude?: number | null;
        longitude?: number | null;
        display?: string | null;
        altitude?: number | null;
      }>;
      uploads?: Array<{ id: string }>;
      references?: Array<{ uuid: string }>;
      factiiiReferences?: Array<{
        factiiiId: number;
        data?: string | null;
      }>;
    }>
  ): Promise<void> {
    for (const factiii of factiiis) {
      // Find existing count for this post+factiiiId
      const existingCounts = await this.db
        .select()
        .from(schema.privatePostFactiiiCounts)
        .where(
          and(
            eq(schema.privatePostFactiiiCounts.postId, postId),
            eq(schema.privatePostFactiiiCounts.factiiiId, factiii.factiiiId)
          )
        )
        .limit(1);

      if (existingCounts.length === 0) continue;

      const fc = existingCounts[0];

      // Find the postFactiii record
      const existingPfs = await this.db
        .select()
        .from(schema.privatePostFactiiis)
        .where(eq(schema.privatePostFactiiis.postFactiiiCountId, fc.id))
        .limit(1);

      if (existingPfs.length === 0) continue;

      const pf = existingPfs[0];

      // Update data, number + anonymous
      await this.db
        .update(schema.privatePostFactiiis)
        .set({
          anonymous: factiii.anonymous || false,
          data: factiii.data !== undefined ? (factiii.data ?? null) : pf.data,
          number:
            factiii.number !== undefined ? (factiii.number ?? null) : pf.number
        })
        .where(eq(schema.privatePostFactiiis.id, pf.id));

      // Replace space times (the replaced rows are deleted with tombstones,
      // or they come back from the other device beside the new ones)
      const oldSpaceTimes = await this.db
        .select({ id: schema.spaceTimes.id })
        .from(schema.spaceTimes)
        .where(eq(schema.spaceTimes.postFactiiiId, pf.id));
      await deleteEachWithTombstones(
        this.db,
        oldSpaceTimes.map((st) => ({
          table: SYNC_TABLES.SPACE_TIMES,
          id: st.id
        }))
      );

      if (factiii.spaceTimes?.length) {
        for (const st of factiii.spaceTimes) {
          const tsValue = st.timestamp
            ? st.timestamp instanceof Date
              ? st.timestamp.getTime()
              : new Date(st.timestamp as unknown as string).getTime()
            : null;
          await this.db.insert(schema.spaceTimes).values({
            postFactiiiId: pf.id,
            timestamp: tsValue,
            latitude: st.latitude ?? null,
            longitude: st.longitude ?? null,
            display: st.display ?? null,
            altitude: st.altitude ?? null,
            source: this.source
          });
        }
      }

      // Replace upload junctions (deleted with tombstones, as above)
      const oldJunctions = await this.db
        .select({ id: schema.privatePostFactiiiUploads.id })
        .from(schema.privatePostFactiiiUploads)
        .where(eq(schema.privatePostFactiiiUploads.postFactiiiId, pf.id));
      await deleteEachWithTombstones(
        this.db,
        oldJunctions.map((junction) => ({
          table: SYNC_TABLES.PRIVATE_POST_FACTIII_UPLOADS,
          id: junction.id
        }))
      );

      if (factiii.uploads?.length) {
        for (const upload of factiii.uploads) {
          await this.db.insert(schema.privatePostFactiiiUploads).values({
            postFactiiiId: pf.id,
            uploadId: upload.id,
            source: this.source
          });
        }
      }

      // Replace post references
      if (factiii.references !== undefined) {
        await this.db
          .delete(schema.privatePostFactiiiPostReferences)
          .where(
            eq(schema.privatePostFactiiiPostReferences.postFactiiiId, pf.id)
          );

        if (factiii.references?.length) {
          for (const ref of factiii.references) {
            await this.db
              .insert(schema.privatePostFactiiiPostReferences)
              .values({
                postFactiiiId: pf.id,
                postUuid: ref.uuid,
                source: this.source
              });
          }
        }
      }

      // Replace factiii references
      if (factiii.factiiiReferences !== undefined) {
        // Delete existing references and their target postFactiiis
        const existingRefs = await this.db
          .select()
          .from(schema.privatePostFactiiiReferences)
          .where(
            eq(schema.privatePostFactiiiReferences.sourcePostFactiiiId, pf.id)
          );

        // Delete target postFactiiis (cascade will delete the references).
        // postFactiiis sync, so each goes with a tombstone.
        await deleteEachWithTombstones(
          this.db,
          existingRefs.map((ref) => ({
            table: SYNC_TABLES.PRIVATE_POST_FACTIIIS,
            id: ref.targetPostFactiiiId
          }))
        );

        // Create new references
        if (factiii.factiiiReferences?.length) {
          for (const ref of factiii.factiiiReferences) {
            const [{ id: targetPfId }] = await this.db
              .insert(schema.privatePostFactiiis)
              .values({
                anonymous: false,
                data: ref.data ?? null,
                source: this.source
              })
              .returning({ id: schema.privatePostFactiiis.id });

            await this.db.insert(schema.privatePostFactiiiReferences).values({
              factiiiId: ref.factiiiId,
              sourcePostFactiiiId: pf.id,
              targetPostFactiiiId: targetPfId,
              source: this.source
            });
          }
        }
      }
    }
  }

  async removeFactiiis(postId: string, factiiiIds: number[]): Promise<void> {
    for (const factiiiId of factiiiIds) {
      const counts = await this.db
        .select({ id: schema.privatePostFactiiiCounts.id })
        .from(schema.privatePostFactiiiCounts)
        .where(
          and(
            eq(schema.privatePostFactiiiCounts.postId, postId),
            eq(schema.privatePostFactiiiCounts.factiiiId, factiiiId)
          )
        );

      // One delete per count — its factiiis, junctions and spaceTimes (what FK
      // CASCADE would take) go with it, each with a tombstone under the count
      await deleteEachWithTombstones(
        this.db,
        counts.map((fc) => ({
          table: SYNC_TABLES.PRIVATE_POST_FACTIII_COUNTS,
          id: fc.id
        }))
      );
    }
  }

  /**
   * Get factiii counts for a post, with nested factiii data
   */
  async getFactiiiCountsForPost(postId: string, _userId: number) {
    const result = await this.db.query.privatePostFactiiiCounts.findMany({
      where: eq(schema.privatePostFactiiiCounts.postId, postId),
      with: {
        factiiis: {
          with: {
            spaceTimes: true,
            uploadJunctions: { with: { upload: true } },
            postReferences: true,
            factiiiReferences: {
              with: {
                targetFactiii: {
                  with: {
                    spaceTimes: true,
                    uploadJunctions: { with: { upload: true } }
                  }
                }
              }
            }
          }
        }
      }
    });

    return (result || []).map((fc) => ({
      id: fc.factiiiId,
      postId: fc.postId,
      factiiiId: fc.factiiiId,
      count: fc.count,
      source: fc.source,
      createdAt: new Date(fc.createdAt),
      updatedAt: new Date(fc.updatedAt),
      postFactiiis: (fc.factiiis || []).map((pf, pfIdx) => ({
        id: pfIdx,
        postFactiiiCountId: fc.factiiiId,
        anonymous: pf.anonymous,
        data: pf.data,
        number: pf.number,
        spaceTimes: (pf.spaceTimes || []).map((st, stIdx) => ({
          id: stIdx,
          timestamp: st.timestamp ? new Date(st.timestamp) : null,
          latitude: st.latitude,
          longitude: st.longitude,
          display: st.display,
          altitude: st.altitude,
          createdAt: new Date(st.createdAt),
          updatedAt: new Date(st.updatedAt)
        })),
        source: pf.source,
        createdAt: new Date(pf.createdAt),
        updatedAt: new Date(pf.updatedAt),
        uploads: (pf.uploadJunctions || [])
          .filter((j) => j.upload)
          .map((j) => ({
            id: j.upload!.id,
            postId: j.upload!.postId,
            key: j.upload!.key,
            type: j.upload!.type as UploadType,
            source: j.upload!.source,
            createdAt: new Date(j.upload!.createdAt)
          })),
        references: (pf.postReferences || []).map((ref) => ({
          uuid: ref.postUuid
        })),
        factiiiReferences: (pf.factiiiReferences || []).map((ref) => ({
          factiiiId: ref.factiiiId,
          data: ref.targetFactiii?.data ?? null,
          spaceTimes: (ref.targetFactiii?.spaceTimes || []).map(
            (st, stIdx) => ({
              id: stIdx,
              timestamp: st.timestamp ? new Date(st.timestamp) : null,
              latitude: st.latitude,
              longitude: st.longitude,
              display: st.display,
              altitude: st.altitude,
              createdAt: new Date(st.createdAt),
              updatedAt: new Date(st.updatedAt)
            })
          ),
          uploads: (ref.targetFactiii?.uploadJunctions || [])
            .filter((j) => j.upload)
            .map((j) => ({
              id: j.upload!.id,
              key: j.upload!.key
            }))
        }))
      }))
    }));
  }

  // ============================================
  // Snapshots
  // ============================================

  /**
   * Collect current post state into a PostSnapshot (not stored, for hashing).
   * Uses a single relational query to fetch all nested data.
   */
  async collectPostSnapshot(postId: string): Promise<PostSnapshot | null> {
    const result = await this.db.query.privatePosts.findFirst({
      where: eq(schema.privatePosts.id, postId),
      with: {
        uploads: true,
        factiiiCounts: {
          with: {
            factiiis: {
              with: {
                spaceTimes: true,
                uploadJunctions: { with: { upload: true } },
                postReferences: true,
                factiiiReferences: {
                  with: {
                    targetFactiii: {
                      with: {
                        spaceTimes: true,
                        uploadJunctions: { with: { upload: true } }
                      }
                    }
                  }
                }
              }
            }
          }
        }
      }
    });

    if (!result) return null;

    const snapshot = {
      post: {
        id: result.id,
        content: result.content,
        spaceId: result.spaceId,
        parentPostId: result.parentPostId,
        anonymous: result.anonymous,
        createdAt: result.createdAt
      },
      uploads: (result.uploads || []).map((u) => ({
        id: u.id,
        key: u.key,
        type: u.type,
        createdAt: u.createdAt
      })),
      factiiis: (result.factiiiCounts || []).map((fc) => ({
        countId: fc.id,
        factiiiId: fc.factiiiId,
        count: fc.count,
        createdAt: fc.createdAt,
        postFactiiis: (fc.factiiis || []).map((pf) => ({
          id: pf.id,
          data: pf.data,
          // Left out when null, so a snapshot hashed before the column existed
          // serializes to the same JSON and still verifies.
          ...(pf.number !== null ? { number: pf.number } : {}),
          anonymous: pf.anonymous,
          createdAt: pf.createdAt,
          spaceTimes: (pf.spaceTimes || []).map((st) => ({
            id: st.id,
            timestamp: st.timestamp,
            latitude: st.latitude,
            longitude: st.longitude,
            display: st.display,
            altitude: st.altitude,
            createdAt: st.createdAt
          })),
          uploads: (pf.uploadJunctions || [])
            .filter((j) => j.upload)
            .map((j) => ({
              uploadId: j.uploadId,
              key: j.upload!.key,
              type: j.upload!.type,
              createdAt: j.createdAt
            })),
          postReferences: (pf.postReferences || []).map((ref) => ({
            postUuid: ref.postUuid,
            createdAt: ref.createdAt
          })),
          factiiiReferences: (pf.factiiiReferences || []).map((ref) => ({
            factiiiId: ref.factiiiId,
            data: ref.targetFactiii?.data ?? null,
            spaceTimes: (ref.targetFactiii?.spaceTimes || []).map((st) => ({
              id: st.id,
              timestamp: st.timestamp,
              latitude: st.latitude,
              longitude: st.longitude,
              display: st.display,
              altitude: st.altitude,
              createdAt: st.createdAt
            })),
            uploads: (ref.targetFactiii?.uploadJunctions || [])
              .filter((j) => j.upload)
              .map((j) => ({
                uploadId: j.uploadId,
                key: j.upload!.key,
                type: j.upload!.type,
                createdAt: j.createdAt
              })),
            createdAt: ref.createdAt
          }))
        }))
      }))
    };

    return normalizeSnapshot({ ...snapshot, createdAt: 0 });
  }

  /**
   * Create and store a snapshot, compute its hash.
   */
  async createAndStoreSnapshot(postId: string): Promise<{
    snapshotId: string;
    hash: string;
    snapshot: PostSnapshot;
  } | null> {
    const postData = await this.collectPostSnapshot(postId);
    if (!postData) return null;

    // Create main snapshot record
    const [{ id: snapshotId }] = await this.db
      .insert(schema.privatePostSnapshots)
      .values({
        postId,
        content: postData.post.content,
        spaceId: postData.post.spaceId,
        parentPostId: postData.post.parentPostId,
        anonymous: postData.post.anonymous,
        postCreatedAt: postData.post.createdAt,
        source: this.source
      })
      .returning({ id: schema.privatePostSnapshots.id });

    // Create child records
    for (const upload of postData.uploads) {
      await this.db.insert(schema.snapshotUploads).values({
        snapshotId,
        uploadId: upload.id,
        key: upload.key,
        type: upload.type,
        uploadCreatedAt: upload.createdAt
      });
    }

    for (const fc of postData.factiiis) {
      const [{ id: countRecordId }] = await this.db
        .insert(schema.snapshotFactiiiCounts)
        .values({
          snapshotId,
          countId: fc.countId,
          factiiiId: fc.factiiiId,
          count: fc.count,
          countCreatedAt: fc.createdAt
        })
        .returning({ id: schema.snapshotFactiiiCounts.id });

      for (const pf of fc.postFactiiis) {
        const [{ id: factiiiRecordId }] = await this.db
          .insert(schema.snapshotFactiiis)
          .values({
            snapshotFactiiiCountId: countRecordId,
            factiiiId: pf.id,
            data: pf.data,
            number: pf.number ?? null,
            anonymous: pf.anonymous,
            factiiiCreatedAt: pf.createdAt
          })
          .returning({ id: schema.snapshotFactiiis.id });

        for (const st of pf.spaceTimes) {
          await this.db.insert(schema.snapshotSpaceTimes).values({
            snapshotFactiiiId: factiiiRecordId,
            spaceTimeId: st.id,
            timestamp: st.timestamp,
            latitude: st.latitude,
            longitude: st.longitude,
            display: st.display,
            altitude: st.altitude,
            spaceTimeCreatedAt: st.createdAt
          });
        }

        for (const fu of pf.uploads) {
          await this.db.insert(schema.snapshotFactiiiUploads).values({
            snapshotFactiiiId: factiiiRecordId,
            uploadId: fu.uploadId,
            key: fu.key,
            type: fu.type,
            uploadCreatedAt: fu.createdAt
          });
        }

        for (const ref of pf.postReferences) {
          await this.db.insert(schema.snapshotPostReferences).values({
            snapshotFactiiiId: factiiiRecordId,
            postUuid: ref.postUuid
          });
        }

        for (const ref of pf.factiiiReferences) {
          await this.db.insert(schema.snapshotFactiiiReferences).values({
            snapshotFactiiiId: factiiiRecordId,
            factiiiId: ref.factiiiId,
            data: ref.data
          });
        }
      }
    }

    // Now read back the stored snapshot and compute hash
    const storedSnapshot = await this.collectStoredSnapshot(snapshotId);
    if (!storedSnapshot) return null;

    const hash = await computeHash(
      JSON.stringify(normalizeSnapshot(storedSnapshot))
    );

    return { snapshotId, hash, snapshot: storedSnapshot };
  }

  /**
   * Reconstruct a stored snapshot from the child tables.
   * Uses a single relational query to fetch all nested data.
   */
  async collectStoredSnapshot(
    snapshotId: string
  ): Promise<PostSnapshot | null> {
    const result = await this.db.query.privatePostSnapshots.findFirst({
      where: eq(schema.privatePostSnapshots.id, snapshotId),
      with: {
        uploads: true,
        factiiiCounts: {
          with: {
            factiiis: {
              with: {
                spaceTimes: true,
                uploads: true,
                postReferences: true,
                factiiiReferences: true
              }
            }
          }
        }
      }
    });

    if (!result) return null;

    return normalizeSnapshot({
      post: {
        id: result.postId,
        content: result.content,
        spaceId: result.spaceId,
        parentPostId: result.parentPostId,
        anonymous: result.anonymous,
        createdAt: result.postCreatedAt
      },
      uploads: (result.uploads || []).map((u) => ({
        id: u.uploadId,
        key: u.key,
        type: u.type,
        createdAt: u.uploadCreatedAt
      })),
      factiiis: (result.factiiiCounts || []).map((fc) => ({
        countId: fc.countId,
        factiiiId: fc.factiiiId,
        count: fc.count,
        createdAt: fc.countCreatedAt,
        postFactiiis: (fc.factiiis || []).map((pf) => ({
          id: pf.factiiiId,
          data: pf.data,
          // Absent when null, matching the snapshot as it was hashed.
          ...(pf.number !== null ? { number: pf.number } : {}),
          anonymous: pf.anonymous,
          createdAt: pf.factiiiCreatedAt,
          spaceTimes: (pf.spaceTimes || []).map((st) => ({
            id: st.spaceTimeId,
            timestamp: st.timestamp,
            latitude: st.latitude,
            longitude: st.longitude,
            display: st.display,
            altitude: st.altitude,
            createdAt: st.spaceTimeCreatedAt
          })),
          uploads: (pf.uploads || []).map((fu) => ({
            uploadId: fu.uploadId,
            key: fu.key,
            type: fu.type,
            createdAt: fu.uploadCreatedAt
          })),
          postReferences: (pf.postReferences || []).map((ref) => ({
            postUuid: ref.postUuid,
            createdAt: ref.createdAt
          })),
          factiiiReferences: (pf.factiiiReferences || []).map((ref) => ({
            factiiiId: ref.factiiiId,
            data: ref.data,
            createdAt: ref.createdAt
          }))
        }))
      })),
      createdAt: result.createdAt
    });
  }

  /**
   * Delete all snapshots for a post.
   * FK CASCADE handles all child records (uploads, factiiiCounts, factiiis, spaceTimes, factiiiUploads).
   */
  async deleteSnapshotsForPost(postId: string): Promise<string[]> {
    const snapshots = await this.db
      .select({ id: schema.privatePostSnapshots.id })
      .from(schema.privatePostSnapshots)
      .where(eq(schema.privatePostSnapshots.postId, postId));

    const snapshotIds = snapshots.map((s) => s.id);
    if (snapshotIds.length === 0) return [];

    // Single delete — FK CASCADE handles all child records
    await this.db
      .delete(schema.privatePostSnapshots)
      .where(eq(schema.privatePostSnapshots.postId, postId));

    return snapshotIds;
  }

  /**
   * Get snapshots sorted for edit history
   */
  getSnapshotsForPost(postId: string) {
    return this.db
      .select()
      .from(schema.privatePostSnapshots)
      .where(eq(schema.privatePostSnapshots.postId, postId))
      .orderBy(desc(schema.privatePostSnapshots.createdAt));
  }

  /**
   * Get unsent snapshots (hash_sent is null)
   */
  getUnsentSnapshots() {
    return this.db
      .select()
      .from(schema.privatePostSnapshots)
      .where(isNull(schema.privatePostSnapshots.hashSent));
  }

  /**
   * Mark a snapshot's hash as sent
   */
  async markHashSent(snapshotId: string, timestamp: number): Promise<void> {
    await this.db
      .update(schema.privatePostSnapshots)
      .set({ hashSent: timestamp })
      .where(eq(schema.privatePostSnapshots.id, snapshotId));
  }

  // ============================================
  // Health Data
  // ============================================

  async getHealthPostsByDateRange(
    userId: number,
    startDate: Date,
    endDate: Date
  ): Promise<
    Array<{
      date: string;
      steps: number;
      exerciseMinutes: number;
      activeCalories: number;
    }>
  > {
    const start = new Date(startDate);
    start.setHours(0, 0, 0, 0);
    const end = new Date(endDate);
    end.setHours(23, 59, 59, 999);

    // Find health posts in the date range
    const healthPosts = await this.db.query.privatePosts.findMany({
      where: (posts, { like, and: andOp, eq: eqOp, between: betweenOp }) =>
        andOp(
          eqOp(posts.userId, userId),
          like(posts.slug, 'health-%'),
          betweenOp(posts.createdAt, start.getTime(), end.getTime())
        ),
      with: {
        factiiiCounts: {
          with: {
            factiiis: true
          }
        }
      }
    });

    return healthPosts.map((post) => {
      const result = {
        date: post.slug?.replace('health-', '') ?? '',
        steps: 0,
        exerciseMinutes: 0,
        activeCalories: 0
      };

      for (const fc of post.factiiiCounts || []) {
        const value = Number(fc.factiiis?.[0]?.data ?? 0);
        // We identify factiiis by name pattern from the slug since we don't
        // have the factiii metadata here. The health factiii stores the date,
        // steps/exercise/calories store numeric values.
        // We use a heuristic: the health factiii data looks like a date (YYYY-MM-DD),
        // so we skip it. For the others, we check the order they were added.
        // A more robust approach: query based on factiiiId mapping.
        // For now, use the post content pattern and the factiii data values.
        if (
          fc.factiiis?.[0]?.data &&
          /^\d{4}-\d{2}-\d{2}$/.test(fc.factiiis[0].data)
        ) {
          // This is the health/date factiii, extract date
          result.date = fc.factiiis[0].data;
        } else if (value > 0) {
          // Assign based on magnitude heuristics:
          // steps are typically thousands, calories hundreds, exercise minutes tens
          // This is fragile — a better approach stores factiiiId mapping
          // We'll use the factiii count order (steps first, exercise second, calories third)
          if (result.steps === 0) {
            result.steps = value;
          } else if (result.exerciseMinutes === 0) {
            result.exerciseMinutes = value;
          } else if (result.activeCalories === 0) {
            result.activeCalories = value;
          }
        }
      }

      return result;
    });
  }

  // ============================================
  // Clear / Debug
  // ============================================

  /**
   * Clear all data from all tables.
   * FK CASCADE handles child tables when root tables are deleted.
   *
   * Writes NO tombstones, and forgets the ones held: this is a wipe of this
   * device's copy, not the user deleting records. Web's "Transfer to Mobile"
   * calls it right after handing everything to the phone; a tombstone here
   * would delete that data from the phone on the next sync.
   */
  async clearEntireDatabase(): Promise<void> {
    // Delete root table — FK CASCADE handles all child tables
    // (factiiiCounts, factiiis, junctions, spaceTimes, uploads [SET NULL],
    //  snapshots + all snapshot children)
    await this.db.delete(schema.privatePosts);
    // Delete remaining independent/orphaned tables
    await this.db.delete(schema.privateUploads);
    await this.db.delete(schema.moodEntries);
    await this.db.delete(schema.factiiiEngagements);
    await this.db.delete(schema.postEngagements);
    await clearTombstones(this.db);
  }

  /**
   * Get counts for all tables
   */
  async getDebugStats() {
    const [pe, fe, me, pp, pu, ppfc, ppf, ppfu, st] = await Promise.all([
      this.db.select({ count: count() }).from(schema.postEngagements),
      this.db.select({ count: count() }).from(schema.factiiiEngagements),
      this.db.select({ count: count() }).from(schema.moodEntries),
      this.db.select({ count: count() }).from(schema.privatePosts),
      this.db.select({ count: count() }).from(schema.privateUploads),
      this.db.select({ count: count() }).from(schema.privatePostFactiiiCounts),
      this.db.select({ count: count() }).from(schema.privatePostFactiiis),
      this.db.select({ count: count() }).from(schema.privatePostFactiiiUploads),
      this.db.select({ count: count() }).from(schema.spaceTimes)
    ]);

    const recentFE = await this.db
      .select()
      .from(schema.factiiiEngagements)
      .orderBy(desc(schema.factiiiEngagements.createdAt))
      .limit(5);

    return {
      counts: {
        postEngagements: pe[0]?.count ?? 0,
        factiiiEngagements: fe[0]?.count ?? 0,
        moodEntries: me[0]?.count ?? 0,
        privatePosts: pp[0]?.count ?? 0,
        privateUploads: pu[0]?.count ?? 0,
        privatePostFactiiiCounts: ppfc[0]?.count ?? 0,
        privatePostFactiiis: ppf[0]?.count ?? 0,
        privatePostFactiiiUploads: ppfu[0]?.count ?? 0,
        spaceTimes: st[0]?.count ?? 0
      },
      recentFactiiiEngagements: recentFE.map((r) => ({
        id: r.id,
        factiiiId: r.factiiiId,
        factiiiName: r.factiiiName,
        viewedAt: r.viewedAt,
        duration: r.duration,
        source: r.source
      }))
    };
  }

  /**
   * Get paginated data from any table
   */
  async getPaginatedTableData(
    tableName: string,
    page: number,
    limit = 20
  ): Promise<{ records: unknown[]; total: number }> {
    // Map table names to schema objects
    const tableMap: Record<string, SQLiteTable> = {
      post_engagements: schema.postEngagements,
      factiii_engagements: schema.factiiiEngagements,
      mood_entries: schema.moodEntries,
      private_posts: schema.privatePosts,
      private_uploads: schema.privateUploads,
      private_post_factiii_counts: schema.privatePostFactiiiCounts,
      private_post_factiiis: schema.privatePostFactiiis,
      private_post_factiii_uploads: schema.privatePostFactiiiUploads,
      space_times: schema.spaceTimes
    };

    const table = tableMap[tableName];
    if (!table) return { records: [], total: 0 };

    const totalResult = await this.db.select({ count: count() }).from(table);
    const records = await this.db
      .select()
      .from(table)
      .orderBy(desc(sql`created_at`))
      .offset(page * limit)
      .limit(limit);

    return {
      records,
      total: totalResult[0]?.count ?? 0
    };
  }

  /**
   * Delete a record by ID and table name
   */
  async deleteRecord(
    tableName: string,
    recordId: string
  ): Promise<string | null> {
    const tableMap: Record<string, SQLiteTable> = {
      post_engagements: schema.postEngagements,
      factiii_engagements: schema.factiiiEngagements,
      mood_entries: schema.moodEntries,
      private_posts: schema.privatePosts,
      private_uploads: schema.privateUploads,
      private_post_factiii_counts: schema.privatePostFactiiiCounts,
      private_post_factiiis: schema.privatePostFactiiis,
      private_post_factiii_uploads: schema.privatePostFactiiiUploads,
      space_times: schema.spaceTimes
    };

    const table = tableMap[tableName];
    if (!table) return null;

    // For uploads, get the blob key before deleting
    let blobKey: string | null = null;
    if (tableName === 'private_uploads') {
      const upload = await this.db
        .select({ key: schema.privateUploads.key })
        .from(schema.privateUploads)
        .where(eq(schema.privateUploads.id, recordId))
        .limit(1);
      blobKey = upload[0]?.key ?? null;
    }

    // A debug delete is still the user deleting a record: it leaves a
    // tombstone (and one for each row FK CASCADE takes with it) like any other
    if (isSyncTableName(tableName)) {
      await deleteWithTombstones(this.db, { table: tableName, id: recordId });
    } else {
      await this.db.delete(table).where(sql`id = ${recordId}`);
    }

    return blobKey;
  }

  /**
   * Delete records with empty key fields (data cleanup)
   */
  async deleteEmptyRecords(): Promise<number> {
    let totalDeleted = 0;

    // Delete post engagements where all key fields are empty
    const emptyPE = await this.db
      .select({ id: schema.postEngagements.id })
      .from(schema.postEngagements)
      .where(
        and(
          or(
            eq(schema.postEngagements.postId, ''),
            isNull(
              schema.postEngagements.postId as unknown as ReturnType<
                typeof text
              >
            )
          ),
          eq(schema.postEngagements.viewedAt, 0),
          eq(schema.postEngagements.duration, 0)
        )
      );

    // Deleted with tombstones: without one the other device hands the empty
    // record straight back
    for (const row of emptyPE) {
      await deleteWithTombstones(this.db, {
        table: SYNC_TABLES.POST_ENGAGEMENTS,
        id: row.id
      });
      totalDeleted++;
    }

    // Delete private posts with empty content
    const emptyPP = await this.db
      .select({ id: schema.privatePosts.id })
      .from(schema.privatePosts)
      .where(
        or(
          eq(schema.privatePosts.content, ''),
          isNull(
            schema.privatePosts.content as unknown as ReturnType<typeof text>
          )
        )
      );

    // No tombstones for posts: "empty content" is a guess at what is junk, and
    // a post can have no text and still carry an image. Deleted here only, the
    // worst case is that a sync brings the post back; with a tombstone it would
    // be deleted, with its replies and files, on the other device too.
    for (const row of emptyPP) {
      await this.db
        .delete(schema.privatePosts)
        .where(eq(schema.privatePosts.id, row.id));
      totalDeleted++;
    }

    return totalDeleted;
  }

  /**
   * Get all upload records (for blob consistency checks)
   */
  async getAllUploadKeys(): Promise<string[]> {
    const rows = await this.db
      .select({ key: schema.privateUploads.key })
      .from(schema.privateUploads);
    return rows.map((r) => r.key);
  }

  /**
   * Delete upload records by keys (for dangling reference cleanup)
   */
  async deleteUploadsByKeys(keys: string[]): Promise<number> {
    if (keys.length === 0) return 0;

    const uploads = await this.db
      .select({ id: schema.privateUploads.id, key: schema.privateUploads.key })
      .from(schema.privateUploads)
      .where(inArray(schema.privateUploads.key, keys));

    if (uploads.length === 0) return 0;

    // No tombstones: this repairs a reference whose blob is missing on THIS
    // device. The other device may hold both the record and the blob, and a
    // tombstone would delete them there. Without one, the next sync brings the
    // record back together with its blob.
    await this.db.delete(schema.privateUploads).where(
      inArray(
        schema.privateUploads.id,
        uploads.map((u) => u.id)
      )
    );

    return uploads.length;
  }

  // ============================================
  // Sync Support
  // ============================================

  /**
   * Get all record IDs and updated_at for sync comparison
   */
  async getAllRecordIds(): Promise<
    Array<{ table: string; records: Array<{ id: string; updated_at: number }> }>
  > {
    const tables = [
      { name: 'post_engagements', table: schema.postEngagements },
      { name: 'factiii_engagements', table: schema.factiiiEngagements },
      { name: 'mood_entries', table: schema.moodEntries },
      { name: 'private_posts', table: schema.privatePosts },
      { name: 'private_uploads', table: schema.privateUploads },
      {
        name: 'private_post_factiii_counts',
        table: schema.privatePostFactiiiCounts
      },
      { name: 'private_post_factiiis', table: schema.privatePostFactiiis },
      {
        name: 'private_post_factiii_uploads',
        table: schema.privatePostFactiiiUploads
      },
      { name: 'space_times', table: schema.spaceTimes }
    ] as const;

    const results = [];
    for (const { name, table } of tables) {
      const rows = await this.db
        .select({ id: table.id, updated_at: table.updatedAt })
        .from(table);
      results.push({
        table: name,
        // Coerce integer IDs to strings for sync compatibility
        records: rows.map((r) => ({
          id: String(r.id),
          updated_at: r.updated_at
        }))
      });
    }

    return results;
  }

  /**
   * Upsert a record from sync data (insert or update)
   */
  async upsertRecord(
    tableName: string,
    id: string,
    data: Record<string, unknown>
  ): Promise<void> {
    // Build values from data, ensuring id is set
    const values = { ...data, id } as Record<string, unknown>;

    // Use INSERT OR REPLACE for upsert behavior
    await this.db.run(
      sql`INSERT OR REPLACE INTO ${sql.identifier(tableName)} (${sql.join(
        Object.keys(values).map((k) => sql.identifier(k)),
        sql`, `
      )}) VALUES (${sql.join(
        Object.values(values).map((v) => sql`${v}`),
        sql`, `
      )})`
    );
  }
}

import { type SQLiteTable, type text } from 'drizzle-orm/sqlite-core';

// ============================================
// Inferred Type Aliases
// ============================================

/** Inferred from getAllPostEngagements return */
export type PostEngagement = Awaited<
  ReturnType<DatabaseRepository['getAllPostEngagements']>
>[number];

/** Inferred from getAllFactiiiEngagements return */
export type FactiiiEngagement = Awaited<
  ReturnType<DatabaseRepository['getAllFactiiiEngagements']>
>[number];

/** Inferred from getLatestMoodEntry return */
export type MoodEntry = NonNullable<
  Awaited<ReturnType<DatabaseRepository['getLatestMoodEntry']>>
>;

/** Inferred from getFactiiiCountsForPost return */
export type PrivatePostFactiiiCount = Awaited<
  ReturnType<DatabaseRepository['getFactiiiCountsForPost']>
>[number];

/** Inferred from PrivatePostFactiiiCount nested type */
export type PrivatePostFactiii =
  PrivatePostFactiiiCount['postFactiiis'][number];

/** Inferred from PrivatePostFactiii nested type */
export type PrivateUpload = PrivatePostFactiii['uploads'][number];

/** Explicit interface — recursive type (buildPrivatePost calls itself for replies) */
export interface PrivatePost {
  id: string;
  content: string;
  anonymous: boolean;
  spaceId?: number | null;
  userId: number;
  parentPostId?: string | null;
  slug?: string | null;
  source: string;
  createdAt: Date;
  updatedAt: Date;
  uploads: PrivateUpload[];
  factiiisCount: PrivatePostFactiiiCount[];
  replies?: PrivatePost[];
  replies_count: number;
}
